import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react"

const rawBase = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL ?? "http://localhost:3015/api",
  credentials: 'include', // Send cookies with requests
  prepareHeaders: async (headers) => {
    // Cookies are sent automatically, but we'll keep token support for backward compatibility
    const token = localStorage.getItem("adminToken")
    if (token) headers.set("Authorization", `Bearer ${token}`)

    // Add CSRF token to non-GET requests
    const csrfToken = sessionStorage.getItem("csrfToken")
    if (csrfToken) {
      headers.set("x-csrf-token", csrfToken)
    }

    return headers
  },
})

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra,
) => {
  const result = await rawBase(args, api, extra)

  // Handle 401 Unauthorized - clear session and redirect to login
  if (result.error?.status === 401) {
    localStorage.removeItem("adminToken")
    localStorage.removeItem("adminUser")
    sessionStorage.removeItem("csrfToken")
    window.location.href = "/login"
  }

  // Handle 403 Forbidden - insufficient permissions
  if (result.error?.status === 403) {
    console.error("Access forbidden - insufficient permissions")
    // Optionally show a notification to the user
  }

  return result
}

export interface AdminUser {
  id: string; firstName: string; lastName: string; email: string
  phone: string; role: string; isActive: boolean; isVerified: boolean; createdAt: string
}
export interface AdminUserDetails {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  role: string
  isActive: boolean
  isVerified: boolean
  createdAt: string
  updatedAt: string
  businesses: Array<{
    id: string
    name: string
    isVerified: boolean
    isFeatured: boolean
    createdAt: string
  }>
  orders: Array<{
    id: string
    orderNumber: string
    totalAmount: number
    status: string
    isPaid: boolean
    createdAt: string
  }>
}
export interface AdminBusiness {
  id: string; name: string; city: string; state: string
  isVerified: boolean; isFeatured: boolean; ownerUid: string; createdAt: string
}
export interface AdminBusinessDetails {
  id: string
  business_user_uid: string
  business_category_id: string
  business_name: string
  business_description: string
  business_logo: string | null
  isVerified: boolean
  isFeatured: boolean
  business_category_name: string
  owner: {
    uid: string
    name: string
    email: string
    phone: string | null
  } | null
  location: {
    address: string
    city: string
    state: string
    country: string
    latitude: number | null
    longitude: number | null
  }
  contact: {
    email: string | null
    phone: string | null
    secondPhone: string | null
    whatsapp: string | null
    website: string | null
  } | null
  createdAt: string
  updatedAt: string
}
export interface AdminReview {
  id: string; rating: number; comment: string; status: string
  reviewableType: string; reviewableUid: string; reviewerUid: string; createdAt: string
}
export interface Stats {
  totalUsers: number; totalBusinesses: number; verifiedBusinesses: number
  pendingReviews: number; totalRoles: number; totalCategories: number
}
export interface AdminRole { id: string; name: string; description?: string; createdAt?: string }
export interface AdminBusinessCategory { id: string; name: string; description: string; image: string; createdAt: string }
export interface AdminServiceCategory { id: string; name: string; iconUrl: string }
export interface AdminBusinessType { id: string; title: string }
export interface PopularSearch { id: number; query: string; count: number; lastSearchedAt: string }
export interface AdminOrder {
  id: string; userUid: string; orderNumber: string; totalAmount: number
  status: string; isPaid: boolean; paymentStatus: string; createdAt: string
}
export interface AdminTicket {
  id: string; userUid: string; ticketNumber: string; userType: string
  subject: string; description: string; status: string; priority: string
  closedAt?: string; createdAt: string
}
export interface AdminFAQ {
  id: string; category?: string; question: string; answer: string
  isPublished: boolean; createdBy: string; createdByUid: string; createdAt: string
}
export interface AdminCoupon {
  id: string; code: string; name: string; description?: string
  discountType: string; discountValue: number; minOrderAmount: number
  maxDiscountAmount: number; validFrom: string; validTo: string
  usageLimit: number; maxPerUser: number; isActive: boolean; createdAt: string
}
export interface SubscriptionPlan {
  id: string; name: string; description?: string
  price: number; currency: string
  interval: "monthly" | "yearly" | "lifetime"
  intervalCount?: number; trialDays?: number
  maxProducts?: number; maxServices?: number
  isFeatured?: boolean; status: "active" | "inactive"
}
export interface AdminBusinessSubscription {
  id: string; businessUid: string; businessName?: string
  plan: SubscriptionPlan | null
  status: "active" | "cancelled" | "expired" | "trial"
  startDate: string; endDate: string | null; autoRenew: boolean; createdAt: string
}
export interface AdminProduct {
  id: string; name: string; description?: string
  price: number; stock: number
  status: "active" | "inactive" | "draft" | "out_of_stock"
  businessUid: string; businessName?: string
  categoryName?: string
  imageUrl?: string | null
  createdAt: string; updatedAt: string
}
export interface AdminProductDetails {
  id: string
  name: string
  description: string | null
  price: number
  stock: number
  status: "active" | "inactive" | "draft" | "out_of_stock"
  category: { id: string; name: string } | null
  subCategory: { id: string; name: string } | null
  business: { id: string; name: string; ownerUid: string } | null
  owner: {
    uid: string
    name: string
    email: string
    phone: string | null
  } | null
  media: Array<{
    id: string
    type: string
    url: string
    isPrimary: boolean
    sortOrder: number
  }>
  variants: Array<{
    id: string
    name: string
    value: string
    price: number
    stock: number
  }>
  createdAt: string
  updatedAt: string
}
export interface AdminPayment {
  id: string; invoiceUid: string; invoiceNumber?: string
  orderNumber?: string; userUid: string
  userName?: string; userEmail?: string
  method: "card" | "bank_transfer" | "wallet"
  status: "pending" | "success" | "failed"
  amount: number; currency: string
  referenceId: string; authorizationCode?: string
  transactionDate?: string; createdAt: string
}
export interface PaymentStats {
  totalPayments: number
  successfulPayments: number
  failedPayments: number
  pendingPayments: number
  totalRevenue: number
  successRate: string
  paymentMethods: Array<{ method: string; count: number; total: number }>
}
export interface AdminWallet {
  id: string
  ownerType: "user" | "business"
  ownerUid: string
  ownerName?: string
  ownerEmail?: string
  balance: number
  currency: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}
export interface WalletStats {
  totalWallets: number
  activeWallets: number
  userWallets: number
  businessWallets: number
  totalBalance: number
  recentTransactions: number
}
export interface AdminReturn {
  id: string
  orderUid: string
  orderNumber?: string
  orderTotal: number
  itemUid: string
  productName?: string
  productImage?: string
  variantName?: string
  quantity: number
  itemPrice: number
  userUid: string
  userName?: string
  userEmail?: string
  reason?: string
  status: "pending" | "approved" | "rejected" | "completed"
  createdAt: string
  updatedAt: string
}
export interface ReturnStats {
  totalReturns: number
  pendingReturns: number
  approvedReturns: number
  rejectedReturns: number
  completedReturns: number
  totalRefundAmount: number
}
export interface ReturnDetails {
  id: string
  status: "pending" | "approved" | "rejected" | "completed"
  reason?: string
  createdAt: string
  updatedAt: string
  order: {
    uid: string
    number: string
    totalAmount: number
    status: string
    paymentStatus: string
    createdAt: string
  }
  item: {
    uid: string
    productName?: string
    productImage?: string
    productDescription?: string
    variantName?: string
    variantSku?: string
    quantity: number
    price: number
    refundAmount: number
  }
  user: {
    uid: string
    name: string
    email: string
    phone?: string
  }
}
export interface PlatformSetting {
  id: string
  key: string
  value: string
  category: "site" | "email" | "payment" | "security" | "features" | "maintenance"
  description?: string
  isEncrypted: boolean
  updatedAt?: string
}
export interface GroupedSettings {
  site?: PlatformSetting[]
  email?: PlatformSetting[]
  payment?: PlatformSetting[]
  security?: PlatformSetting[]
  features?: PlatformSetting[]
  maintenance?: PlatformSetting[]
}

export interface CommissionAnalytics {
  totalCommission: number
  totalBusinessPayout: number
  totalRevenue: number
  orderCount: number
  currentCommissionRate: number
  averageCommissionPerOrder: number
}

export interface CommissionPeriodData {
  period: string
  commission: number
  businessPayout: number
  revenue: number
  orderCount: number
}

export interface CommissionByBusiness {
  businessId: string
  businessName: string
  commission: number
  businessPayout: number
  orderCount: number
}

export interface AdminEarlyAdopter {
  id: string
  businessName: string
  tier: 'founder' | 'pioneer' | 'explorer'
  joinedAt: string
  ownerName: string
  ownerEmail: string
  totalRevenue: number
  orderCount: number
}

export interface EarlyAdopterStats {
  totalEarlyAdopters: number
  founderCount: number
  pioneerCount: number
  explorerCount: number
  totalRevenue: number
  totalOrders: number
  avgRevenuePerBusiness: number
  programEnabled: boolean
  maxCount: number
  cutoffDate: string
  remainingSlots: number
}

type PaginatedResult<K extends string, T> = Record<K, T[]> & { total: number; totalPages: number; page: number }

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery,
  tagTypes: ["Users", "Businesses", "Reviews", "Stats", "Roles", "BizCategories", "SvcCategories", "BizTypes", "PopSearches", "Orders", "Tickets", "FAQs", "Coupons", "SubPlans", "Subscriptions", "Products", "Payments", "Wallets", "Returns", "Settings", "Commission", "EarlyAdopters"],
  endpoints: (builder) => ({

    // ── Stats ──────────────────────────────────────────────────────────────
    getStats: builder.query<Stats, void>({
      query: () => "/admin/stats",
      transformResponse: (r: any) => r.stats,
      providesTags: ["Stats"],
    }),

    // ── Users ──────────────────────────────────────────────────────────────
    getUsers: builder.query<PaginatedResult<"users", AdminUser>, { page?: number; search?: string }>({
      query: ({ page = 1, search } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (search) q.set("search", search)
        return `/admin/users?${q}`
      },
      transformResponse: (r: any) => ({ users: r.users, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Users"],
    }),
    getUserById: builder.query<AdminUserDetails, string>({
      query: (uid) => `/admin/users/${uid}`,
      transformResponse: (r: any) => r.user,
      providesTags: (_result, _error, uid) => [{ type: "Users", id: uid }],
    }),
    suspendUser: builder.mutation<void, { uid: string; suspend: boolean }>({
      query: ({ uid, suspend }) => ({ url: `/admin/users/${uid}/suspend`, method: "PUT", body: { suspend } }),
      invalidatesTags: ["Users"],
    }),
    getUserRoles: builder.query<{ roles: AdminRole[] }, string>({
      query: (uid) => `/admin/users/${uid}/roles`,
      transformResponse: (r: any) => ({ roles: r.roles }),
      providesTags: (_r, _e, uid) => [{ type: "Users", id: uid }],
    }),
    assignRole: builder.mutation<void, { uid: string; roleUid: string }>({
      query: ({ uid, roleUid }) => ({ url: `/admin/users/${uid}/roles`, method: "POST", body: { roleUid } }),
      invalidatesTags: (_r, _e, { uid }) => [{ type: "Users", id: uid }],
    }),
    revokeRole: builder.mutation<void, { uid: string; roleUid: string }>({
      query: ({ uid, roleUid }) => ({ url: `/admin/users/${uid}/roles/${roleUid}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { uid }) => [{ type: "Users", id: uid }],
    }),

    // ── Businesses ─────────────────────────────────────────────────────────
    getBusinesses: builder.query<PaginatedResult<"businesses", AdminBusiness>, { page?: number; verified?: string }>({
      query: ({ page = 1, verified } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (verified !== undefined) q.set("verified", verified)
        return `/admin/businesses?${q}`
      },
      transformResponse: (r: any) => ({ businesses: r.businesses, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Businesses"],
    }),
    getBusinessById: builder.query<AdminBusinessDetails, string>({
      query: (uid) => `/business/${uid}`,
      transformResponse: (r: any) => r.business,
      providesTags: (_result, _error, uid) => [{ type: "Businesses", id: uid }],
    }),
    verifyBusiness: builder.mutation<void, { uid: string; verify: boolean }>({
      query: ({ uid, verify }) => ({ url: `/admin/businesses/${uid}/verify`, method: "PUT", body: { verify } }),
      invalidatesTags: ["Businesses", "Stats"],
    }),

    // ── Reviews ────────────────────────────────────────────────────────────
    getReviews: builder.query<PaginatedResult<"reviews", AdminReview>, { page?: number; status?: string }>({
      query: ({ page = 1, status = "pending" } = {}) => `/admin/reviews?page=${page}&status=${status}`,
      transformResponse: (r: any) => ({ reviews: r.reviews, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Reviews"],
    }),
    moderateReview: builder.mutation<void, { uid: string; action: "approved" | "rejected" }>({
      query: ({ uid, action }) => ({ url: `/admin/reviews/${uid}/moderate`, method: "PUT", body: { action } }),
      invalidatesTags: ["Reviews", "Stats"],
    }),

    // ── Roles ──────────────────────────────────────────────────────────────
    getRoles: builder.query<AdminRole[], void>({
      query: () => "/admin/roles",
      transformResponse: (r: any) => r.roles,
      providesTags: ["Roles"],
    }),
    createRole: builder.mutation<void, { name: string; description?: string }>({
      query: (body) => ({ url: "/admin/roles", method: "POST", body }),
      invalidatesTags: ["Roles", "Stats"],
    }),
    updateRole: builder.mutation<void, { uid: string; name?: string; description?: string }>({
      query: ({ uid, ...body }) => ({ url: `/admin/roles/${uid}`, method: "PUT", body }),
      invalidatesTags: ["Roles"],
    }),
    deleteRole: builder.mutation<void, string>({
      query: (uid) => ({ url: `/admin/roles/${uid}`, method: "DELETE" }),
      invalidatesTags: ["Roles", "Stats"],
    }),

    // ── Business Categories ────────────────────────────────────────────────
    getBusinessCategories: builder.query<AdminBusinessCategory[], void>({
      query: () => "/admin/categories/business",
      transformResponse: (r: any) => r.categories,
      providesTags: ["BizCategories"],
    }),
    createBusinessCategory: builder.mutation<void, { name: string; description?: string; image?: string }>({
      query: (body) => ({ url: "/admin/categories/business", method: "POST", body }),
      invalidatesTags: ["BizCategories", "Stats"],
    }),
    updateBusinessCategory: builder.mutation<void, { uid: string; name?: string; description?: string; image?: string }>({
      query: ({ uid, ...body }) => ({ url: `/admin/categories/business/${uid}`, method: "PUT", body }),
      invalidatesTags: ["BizCategories"],
    }),
    deleteBusinessCategory: builder.mutation<void, string>({
      query: (uid) => ({ url: `/admin/categories/business/${uid}`, method: "DELETE" }),
      invalidatesTags: ["BizCategories", "Stats"],
    }),

    // ── Service Categories ─────────────────────────────────────────────────
    getServiceCategories: builder.query<AdminServiceCategory[], void>({
      query: () => "/admin/categories/service",
      transformResponse: (r: any) => r.categories,
      providesTags: ["SvcCategories"],
    }),
    createServiceCategory: builder.mutation<void, { name: string; iconUrl?: string }>({
      query: (body) => ({ url: "/admin/categories/service", method: "POST", body }),
      invalidatesTags: ["SvcCategories"],
    }),
    updateServiceCategory: builder.mutation<void, { uid: string; name?: string; iconUrl?: string }>({
      query: ({ uid, ...body }) => ({ url: `/admin/categories/service/${uid}`, method: "PUT", body }),
      invalidatesTags: ["SvcCategories"],
    }),
    deleteServiceCategory: builder.mutation<void, string>({
      query: (uid) => ({ url: `/admin/categories/service/${uid}`, method: "DELETE" }),
      invalidatesTags: ["SvcCategories"],
    }),

    // ── Business Types ─────────────────────────────────────────────────────
    getBusinessTypes: builder.query<AdminBusinessType[], void>({
      query: () => "/admin/types",
      transformResponse: (r: any) => r.types,
      providesTags: ["BizTypes"],
    }),
    createBusinessType: builder.mutation<void, { title: string }>({
      query: (body) => ({ url: "/admin/types", method: "POST", body }),
      invalidatesTags: ["BizTypes"],
    }),
    updateBusinessType: builder.mutation<void, { id: string; title: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/types/${id}`, method: "PUT", body }),
      invalidatesTags: ["BizTypes"],
    }),
    deleteBusinessType: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/types/${id}`, method: "DELETE" }),
      invalidatesTags: ["BizTypes"],
    }),

    // ── Popular Searches ───────────────────────────────────────────────────
    getPopularSearches: builder.query<PopularSearch[], void>({
      query: () => "/admin/searches/popular",
      transformResponse: (r: any) => r.searches,
      providesTags: ["PopSearches"],
    }),
    deletePopularSearch: builder.mutation<void, number>({
      query: (id) => ({ url: `/admin/searches/popular/${id}`, method: "DELETE" }),
      invalidatesTags: ["PopSearches"],
    }),
    clearPopularSearches: builder.mutation<void, void>({
      query: () => ({ url: "/admin/searches/popular", method: "DELETE" }),
      invalidatesTags: ["PopSearches"],
    }),

    // ── Feature Business ───────────────────────────────────────────────────
    featureBusiness: builder.mutation<void, { uid: string; feature: boolean }>({
      query: ({ uid, feature }) => ({ url: `/admin/businesses/${uid}/feature`, method: "PUT", body: { feature } }),
      invalidatesTags: ["Businesses"],
    }),

    // ── Orders ─────────────────────────────────────────────────────────────
    getOrders: builder.query<PaginatedResult<"orders", AdminOrder>, { page?: number; status?: string }>({
      query: ({ page = 1, status } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (status) q.set("status", status)
        return `/admin/orders?${q}`
      },
      transformResponse: (r: any) => ({ orders: r.orders, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Orders"],
    }),

    // ── Support Tickets ────────────────────────────────────────────────────
    getTickets: builder.query<PaginatedResult<"tickets", AdminTicket>, { page?: number; status?: string }>({
      query: ({ page = 1, status } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (status) q.set("status", status)
        return `/admin/tickets?${q}`
      },
      transformResponse: (r: any) => ({ tickets: r.tickets, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Tickets"],
    }),
    updateTicketStatus: builder.mutation<void, { uid: string; status: string }>({
      query: ({ uid, status }) => ({ url: `/admin/tickets/${uid}/status`, method: "PUT", body: { status } }),
      invalidatesTags: ["Tickets"],
    }),

    // ── FAQs ───────────────────────────────────────────────────────────────
    getFAQs: builder.query<AdminFAQ[], { published?: string }>({
      query: ({ published } = {}) => {
        const q = new URLSearchParams()
        if (published !== undefined) q.set("published", published)
        return `/admin/faqs?${q}`
      },
      transformResponse: (r: any) => r.faqs,
      providesTags: ["FAQs"],
    }),
    createFAQ: builder.mutation<void, { question: string; answer: string; category?: string; isPublished?: boolean }>({
      query: (body) => ({ url: "/admin/faqs", method: "POST", body }),
      invalidatesTags: ["FAQs"],
    }),
    updateFAQ: builder.mutation<void, { uid: string; question?: string; answer?: string; category?: string; isPublished?: boolean }>({
      query: ({ uid, ...body }) => ({ url: `/admin/faqs/${uid}`, method: "PUT", body }),
      invalidatesTags: ["FAQs"],
    }),
    deleteFAQ: builder.mutation<void, string>({
      query: (uid) => ({ url: `/admin/faqs/${uid}`, method: "DELETE" }),
      invalidatesTags: ["FAQs"],
    }),

    // ── Coupons ────────────────────────────────────────────────────────────
    getCoupons: builder.query<AdminCoupon[], void>({
      query: () => "/admin/coupons",
      transformResponse: (r: any) => r.coupons,
      providesTags: ["Coupons"],
    }),
    createCoupon: builder.mutation<void, { code: string; name: string; description?: string; discountType: string; discountValue: number; minOrderAmount?: number; maxDiscountAmount?: number; validFrom: string; validTo: string; usageLimit: number; maxPerUser?: number }>({
      query: (body) => ({ url: "/admin/coupons", method: "POST", body }),
      invalidatesTags: ["Coupons"],
    }),
    updateCoupon: builder.mutation<void, { uid: string; name?: string; description?: string; discountValue?: number; minOrderAmount?: number; maxDiscountAmount?: number; validFrom?: string; validTo?: string; usageLimit?: number; maxPerUser?: number; isActive?: boolean }>({
      query: ({ uid, ...body }) => ({ url: `/admin/coupons/${uid}`, method: "PUT", body }),
      invalidatesTags: ["Coupons"],
    }),
    deleteCoupon: builder.mutation<void, string>({
      query: (uid) => ({ url: `/admin/coupons/${uid}`, method: "DELETE" }),
      invalidatesTags: ["Coupons"],
    }),

    // ── Subscription Plans ─────────────────────────────────────────────────
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => "/admin/subscription-plans",
      transformResponse: (r: any) => r.plans ?? [],
      providesTags: ["SubPlans"],
    }),
    createSubscriptionPlan: builder.mutation<SubscriptionPlan, Partial<SubscriptionPlan>>({
      query: (body) => ({ url: "/admin/subscription-plans", method: "POST", body }),
      transformResponse: (r: any) => r.plan,
      invalidatesTags: ["SubPlans"],
    }),
    updateSubscriptionPlan: builder.mutation<SubscriptionPlan, { id: string } & Partial<SubscriptionPlan>>({
      query: ({ id, ...body }) => ({ url: `/admin/subscription-plans/${id}`, method: "PUT", body }),
      transformResponse: (r: any) => r.plan,
      invalidatesTags: ["SubPlans"],
    }),
    deleteSubscriptionPlan: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/subscription-plans/${id}`, method: "DELETE" }),
      invalidatesTags: ["SubPlans"],
    }),

    // ── Business Subscriptions ─────────────────────────────────────────────
    getBusinessSubscriptions: builder.query<{ subscriptions: AdminBusinessSubscription[]; total: number }, { page?: number; status?: string }>({
      query: ({ page = 1, status } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (status) q.set("status", status)
        return `/admin/subscriptions?${q}`
      },
      transformResponse: (r: any) => ({ subscriptions: r.subscriptions ?? [], total: r.total ?? 0 }),
      providesTags: ["Subscriptions"],
    }),
    assignBusinessSubscription: builder.mutation<void, { businessUid: string; planId: string }>({
      query: ({ businessUid, planId }) => ({
        url: `/admin/businesses/${businessUid}/subscription`,
        method: "PUT",
        body: { plan_uid: planId },
      }),
      invalidatesTags: ["Subscriptions"],
    }),

    // ── Products ───────────────────────────────────────────────────────────
    getProducts: builder.query<PaginatedResult<"products", AdminProduct>, { page?: number; search?: string; status?: string; businessUid?: string }>({
      query: ({ page = 1, search, status, businessUid } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (search) q.set("search", search)
        if (status) q.set("status", status)
        if (businessUid) q.set("businessUid", businessUid)
        return `/admin/products?${q}`
      },
      transformResponse: (r: any) => ({ products: r.products, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Products"],
    }),
    getProductById: builder.query<AdminProductDetails, string>({
      query: (uid) => `/admin/products/${uid}`,
      transformResponse: (r: any) => r.product,
      providesTags: (_result, _error, uid) => [{ type: "Products", id: uid }],
    }),
    moderateProduct: builder.mutation<void, { uid: string; action: "approve" | "reject" }>({
      query: ({ uid, action }) => ({ url: `/admin/products/${uid}/moderate`, method: "PUT", body: { action } }),
      invalidatesTags: ["Products"],
    }),
    updateProductStatus: builder.mutation<void, { uid: string; status: string }>({
      query: ({ uid, status }) => ({ url: `/admin/products/${uid}/status`, method: "PUT", body: { status } }),
      invalidatesTags: ["Products"],
    }),

    // ── Payments & Transactions ───────────────────────────────────────────
    getPayments: builder.query<PaginatedResult<"payments", AdminPayment>, { page?: number; status?: string; method?: string; dateFrom?: string; dateTo?: string; search?: string }>({
      query: ({ page = 1, status, method, dateFrom, dateTo, search } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (status) q.set("status", status)
        if (method) q.set("method", method)
        if (dateFrom) q.set("dateFrom", dateFrom)
        if (dateTo) q.set("dateTo", dateTo)
        if (search) q.set("search", search)
        return `/admin/payments?${q}`
      },
      transformResponse: (r: any) => ({ payments: r.payments, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Payments"],
    }),
    getPaymentStats: builder.query<PaymentStats, { dateFrom?: string; dateTo?: string }>({
      query: ({ dateFrom, dateTo } = {}) => {
        const q = new URLSearchParams()
        if (dateFrom) q.set("dateFrom", dateFrom)
        if (dateTo) q.set("dateTo", dateTo)
        return `/admin/payments/stats?${q}`
      },
      transformResponse: (r: any) => r.stats,
      providesTags: ["Payments"],
    }),
    getPaymentDetails: builder.query<any, string>({
      query: (uid) => `/admin/payments/${uid}`,
      transformResponse: (r: any) => r.payment,
      providesTags: (_r, _e, uid) => [{ type: "Payments", id: uid }],
    }),

    // ── Wallet Management ──────────────────────────────────────────────────
    getWallets: builder.query<PaginatedResult<"wallets", AdminWallet>, { page?: number; ownerType?: string; search?: string; minBalance?: number; maxBalance?: number }>({
      query: ({ page = 1, ownerType, search, minBalance, maxBalance } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (ownerType) q.set("ownerType", ownerType)
        if (search) q.set("search", search)
        if (minBalance !== undefined) q.set("minBalance", String(minBalance))
        if (maxBalance !== undefined) q.set("maxBalance", String(maxBalance))
        return `/admin/wallets?${q}`
      },
      transformResponse: (r: any) => ({ wallets: r.wallets, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Wallets"],
    }),
    getWalletStats: builder.query<WalletStats, void>({
      query: () => "/admin/wallets/stats",
      transformResponse: (r: any) => r.stats,
      providesTags: ["Wallets"],
    }),
    getWalletDetails: builder.query<any, string>({
      query: (uid) => `/admin/wallets/${uid}`,
      transformResponse: (r: any) => r.wallet,
      providesTags: (_r, _e, uid) => [{ type: "Wallets", id: uid }],
    }),
    requestWalletAdjustmentOTP: builder.mutation<{ success: boolean; message: string; expiresIn: number }, { uid: string; amount: number; reason: string }>({
      query: ({ uid, amount, reason }) => ({ url: `/admin/wallets/${uid}/request-otp`, method: "POST", body: { amount, reason } }),
    }),
    adjustWalletBalance: builder.mutation<void, { uid: string; amount: number; reason: string; otp: string }>({
      query: ({ uid, amount, reason, otp }) => ({ url: `/admin/wallets/${uid}/adjust`, method: "POST", body: { amount, reason, otp } }),
      invalidatesTags: ["Wallets"],
    }),
    toggleWalletStatus: builder.mutation<void, { uid: string; isActive: boolean }>({
      query: ({ uid, isActive }) => ({ url: `/admin/wallets/${uid}/status`, method: "PUT", body: { isActive } }),
      invalidatesTags: ["Wallets"],
    }),

    // ── Returns Management ─────────────────────────────────────────────────
    getReturns: builder.query<PaginatedResult<"returns", AdminReturn>, { page?: number; status?: string; search?: string; dateFrom?: string; dateTo?: string }>({
      query: ({ page = 1, status, search, dateFrom, dateTo } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (status) q.set("status", status)
        if (search) q.set("search", search)
        if (dateFrom) q.set("dateFrom", dateFrom)
        if (dateTo) q.set("dateTo", dateTo)
        return `/admin/returns?${q}`
      },
      transformResponse: (r: any) => ({ returns: r.returns, total: r.total, totalPages: r.totalPages, page: r.page }),
      providesTags: ["Returns"],
    }),
    getReturnStats: builder.query<ReturnStats, void>({
      query: () => "/admin/returns/stats",
      transformResponse: (r: any) => r.stats,
      providesTags: ["Returns"],
    }),
    getReturnDetails: builder.query<ReturnDetails, string>({
      query: (uid) => `/admin/returns/${uid}`,
      transformResponse: (r: any) => r.return,
      providesTags: (_r, _e, uid) => [{ type: "Returns", id: uid }],
    }),
    updateReturnStatus: builder.mutation<void, { uid: string; status: "pending" | "approved" | "rejected" | "completed"; adminNotes?: string }>({
      query: ({ uid, status, adminNotes }) => ({ url: `/admin/returns/${uid}/status`, method: "PUT", body: { status, adminNotes } }),
      invalidatesTags: ["Returns"],
    }),

    // ── Platform Settings ──────────────────────────────────────────────────
    getSettings: builder.query<GroupedSettings, { category?: string }>({
      query: ({ category } = {}) => {
        const q = new URLSearchParams()
        if (category) q.set("category", category)
        return `/admin/settings${q.toString() ? `?${q}` : ""}`
      },
      transformResponse: (r: any) => r.settings,
      providesTags: ["Settings"],
    }),
    getSetting: builder.query<PlatformSetting, string>({
      query: (key) => `/admin/settings/${key}`,
      transformResponse: (r: any) => r.setting,
      providesTags: (_r, _e, key) => [{ type: "Settings", id: key }],
    }),
    createSetting: builder.mutation<void, { key: string; value: string; category: string; description?: string; isEncrypted?: boolean }>({
      query: (body) => ({ url: "/admin/settings", method: "POST", body }),
      invalidatesTags: ["Settings"],
    }),
    updateSetting: builder.mutation<void, { key: string; value: string }>({
      query: ({ key, value }) => ({ url: `/admin/settings/${key}`, method: "PUT", body: { value } }),
      invalidatesTags: ["Settings"],
    }),
    deleteSetting: builder.mutation<void, string>({
      query: (key) => ({ url: `/admin/settings/${key}`, method: "DELETE" }),
      invalidatesTags: ["Settings"],
    }),
    bulkUpdateSettings: builder.mutation<void, { settings: Array<{ key: string; value: string }> }>({
      query: (body) => ({ url: "/admin/settings/bulk", method: "POST", body }),
      invalidatesTags: ["Settings"],
    }),

    // Commission & Revenue
    getCommissionAnalytics: builder.query<CommissionAnalytics, { startDate?: string; endDate?: string } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams()
        if (params?.startDate) queryParams.append("startDate", params.startDate)
        if (params?.endDate) queryParams.append("endDate", params.endDate)
        const queryString = queryParams.toString()
        return `/admin/commission/analytics${queryString ? `?${queryString}` : ""}`
      },
      transformResponse: (r: any) => r.analytics,
      providesTags: ["Commission"],
    }),
    getCommissionByPeriod: builder.query<CommissionPeriodData[], { period?: "day" | "week" | "month" | "year"; limit?: number }>({
      query: (params) => {
        const queryParams = new URLSearchParams()
        if (params?.period) queryParams.append("period", params.period)
        if (params?.limit) queryParams.append("limit", String(params.limit))
        return `/admin/commission/by-period?${queryParams.toString()}`
      },
      transformResponse: (r: any) => r.data,
      providesTags: ["Commission"],
    }),
    getCommissionByBusiness: builder.query<CommissionByBusiness[], { limit?: number }>({
      query: (params) => {
        const queryParams = new URLSearchParams()
        if (params?.limit) queryParams.append("limit", String(params.limit))
        return `/admin/commission/by-business?${queryParams.toString()}`
      },
      transformResponse: (r: any) => r.businesses,
      providesTags: ["Commission"],
    }),
    updateCommissionRate: builder.mutation<{ rate: number; percentage: string }, { rate: number }>({
      query: (body) => ({ url: "/admin/commission/rate", method: "PUT", body }),
      invalidatesTags: ["Commission"],
    }),

    // ── Early Adopters ─────────────────────────────────────────────────────
    getEarlyAdopters: builder.query<PaginatedResult<"earlyAdopters", AdminEarlyAdopter>, { page?: number; tier?: string }>({
      query: ({ page = 1, tier } = {}) => {
        const q = new URLSearchParams({ page: String(page) })
        if (tier) q.set("tier", tier)
        return `/admin/early-adopters?${q}`
      },
      transformResponse: (r: any) => ({
        earlyAdopters: r.earlyAdopters,
        total: r.total,
        totalPages: r.totalPages,
        page: r.page
      }),
      providesTags: ["EarlyAdopters"],
    }),
    getEarlyAdopterStats: builder.query<EarlyAdopterStats, void>({
      query: () => "/admin/early-adopters/stats",
      transformResponse: (r: any) => r.stats,
      providesTags: ["EarlyAdopters"],
    }),
    setEarlyAdopter: builder.mutation<void, { businessUid: string; isEarlyAdopter: boolean; tier?: string }>({
      query: ({ businessUid, isEarlyAdopter, tier }) => ({
        url: `/admin/business/${businessUid}/early-adopter`,
        method: "PUT",
        body: { isEarlyAdopter, tier }
      }),
      invalidatesTags: ["EarlyAdopters", "Businesses", "Stats"],
    }),
  }),
})

export const {
  useGetStatsQuery,
  useGetUsersQuery, useGetUserByIdQuery, useSuspendUserMutation, useGetUserRolesQuery, useAssignRoleMutation, useRevokeRoleMutation,
  useGetBusinessesQuery, useGetBusinessByIdQuery, useVerifyBusinessMutation, useFeatureBusinessMutation,
  useGetReviewsQuery, useModerateReviewMutation,
  useGetRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation,
  useGetBusinessCategoriesQuery, useCreateBusinessCategoryMutation, useUpdateBusinessCategoryMutation, useDeleteBusinessCategoryMutation,
  useGetServiceCategoriesQuery, useCreateServiceCategoryMutation, useUpdateServiceCategoryMutation, useDeleteServiceCategoryMutation,
  useGetBusinessTypesQuery, useCreateBusinessTypeMutation, useUpdateBusinessTypeMutation, useDeleteBusinessTypeMutation,
  useGetPopularSearchesQuery, useDeletePopularSearchMutation, useClearPopularSearchesMutation,
  useGetOrdersQuery,
  useGetTicketsQuery, useUpdateTicketStatusMutation,
  useGetFAQsQuery, useCreateFAQMutation, useUpdateFAQMutation, useDeleteFAQMutation,
  useGetCouponsQuery, useCreateCouponMutation, useUpdateCouponMutation, useDeleteCouponMutation,
  useGetSubscriptionPlansQuery, useCreateSubscriptionPlanMutation, useUpdateSubscriptionPlanMutation, useDeleteSubscriptionPlanMutation,
  useGetBusinessSubscriptionsQuery, useAssignBusinessSubscriptionMutation,
  useGetProductsQuery, useGetProductByIdQuery, useModerateProductMutation, useUpdateProductStatusMutation,
  useGetPaymentsQuery, useGetPaymentStatsQuery, useGetPaymentDetailsQuery,
  useGetWalletsQuery, useGetWalletStatsQuery, useGetWalletDetailsQuery, useRequestWalletAdjustmentOTPMutation, useAdjustWalletBalanceMutation, useToggleWalletStatusMutation,
  useGetReturnsQuery, useGetReturnStatsQuery, useGetReturnDetailsQuery, useUpdateReturnStatusMutation,
  useGetSettingsQuery, useGetSettingQuery, useCreateSettingMutation, useUpdateSettingMutation, useDeleteSettingMutation, useBulkUpdateSettingsMutation,
  useGetCommissionAnalyticsQuery, useGetCommissionByPeriodQuery, useGetCommissionByBusinessQuery, useUpdateCommissionRateMutation,
  useGetEarlyAdoptersQuery, useGetEarlyAdopterStatsQuery, useSetEarlyAdopterMutation,
} = adminApi
